import { useEffect, useState } from "react";
import DocumentMeta from "react-document-meta";
import ExampleLandingHeader from "../components/ExampleLandingHeader";
import { useTranslation } from "../i18n/LanguageContext";
import FloatingWhatsAppButton from "../components/FloatingWhatsAppButton";
import SocialLinksSection from "../components/SocialLinksSection";
import { buildNoIndexMeta } from "../seo";

const copy = {
    it: {
        title: "Mare Azzurro",
        metaTitle: "Ristorante Mare Azzurro | Cucina di pesce",
        metaDescription: "Piatti di pesce fresco e tavoli vista mare. Prenota in modo semplice dal telefono.",
        intro: "Pesce fresco, cucina semplice e tavoli vista mare. Perfetto per pranzi e cene d’estate.",
        menu: [
            { label: "Menù", href: "#menu" },
            { label: "Chef", href: "#chef" },
            { label: "Prenota", href: "#prenota" }
        ],
        ctaPrimary: "Prenota un tavolo",
        ctaSecondary: "Guarda il menù",
        menuTitle: "Menù Mare Azzurro",
        menuSubtitle: "Piatti semplici, sapori di mare e prodotti locali.",
        menuSections: [
            {
                title: "Antipasti",
                items: [
                    { name: "Crudo del giorno", price: "€18" },
                    { name: "Tartare di tonno", price: "€16" },
                    { name: "Polpo e patate", price: "€14" },
                    { name: "Cozze alla marinara", price: "€12" },
                    { name: "Insalata di mare", price: "€15" }
                ]
            },
            {
                title: "Primi",
                items: [
                    { name: "Spaghetti ai ricci", price: "€18" },
                    { name: "Fregola con arselle", price: "€16" },
                    { name: "Linguine allo scoglio", price: "€19" },
                    { name: "Risotto alla pescatora", price: "€18" },
                    { name: "Malloreddus al profumo di mare", price: "€15" }
                ]
            },
            {
                title: "Secondi",
                items: [
                    { name: "Grigliata del pescato", price: "€22" },
                    { name: "Calamari alla piastra", price: "€20" },
                    { name: "Orata al sale", price: "€21" },
                    { name: "Trancio di tonno scottato", price: "€23" },
                    { name: "Frittura mista", price: "€19" }
                ]
            },
            {
                title: "Contorni",
                items: [
                    { name: "Insalata di stagione", price: "€6" },
                    { name: "Patate al forno", price: "€6" },
                    { name: "Verdure grigliate", price: "€7" },
                    { name: "Misticanza al limone", price: "€6" }
                ]
            }
        ],
        pizzaTitle: "Menù Pizze",
        pizzaItems: [
            { name: "Margherita", price: "€8" },
            { name: "Marinara", price: "€7" },
            { name: "Sarda", price: "€11" },
            { name: "Quattro formaggi", price: "€11" },
            { name: "Tonno e cipolla", price: "€10" }
        ],
        wineTitle: "Carta dei vini sardi",
        wineSubtitle: "5 etichette scelte per abbinarsi al pescato.",
        wines: [
            { name: "Vermentino di Gallura DOCG", detail: "Fresco e minerale" },
            { name: "Nuragus di Cagliari DOC", detail: "Delicato e floreale" },
            { name: "Cannonau di Sardegna DOC", detail: "Morbido e speziato" },
            { name: "Carignano del Sulcis DOC", detail: "Strutturato e intenso" },
            { name: "Malvasia di Bosa DOC", detail: "Aromatico e vellutato" }
        ],
        wineImageAlt: "Calice di vino al tramonto sul mare",
        bookTitle: "Prenota il tuo tavolo",
        bookSubtitle: "Risposta veloce via WhatsApp.",
        bookCta1: "Prenota ora",
        bookCta2: "Chiama"
    },
    en: {
        title: "Mare Azzurro",
        metaTitle: "Mare Azzurro | Seafood restaurant",
        metaDescription: "Fresh seafood and sea-view tables. Book easily from your phone.",
        intro: "Fresh seafood, simple cooking and sea-view tables. Perfect for summer lunches and dinners.",
        menu: [
            { label: "Menu", href: "#menu" },
            { label: "Chef", href: "#chef" },
            { label: "Book", href: "#prenota" }
        ],
        ctaPrimary: "Book a table",
        ctaSecondary: "See the menu",
        menuTitle: "Mare Azzurro Menu",
        menuSubtitle: "Simple dishes, sea flavors and local products.",
        menuSections: [
            {
                title: "Starters",
                items: [
                    { name: "Daily raw selection", price: "€18" },
                    { name: "Tuna tartare", price: "€16" },
                    { name: "Octopus and potatoes", price: "€14" },
                    { name: "Mussels marinara", price: "€12" },
                    { name: "Seafood salad", price: "€15" }
                ]
            },
            {
                title: "First courses",
                items: [
                    { name: "Sea urchin spaghetti", price: "€18" },
                    { name: "Fregola with clams", price: "€16" },
                    { name: "Seafood linguine", price: "€19" },
                    { name: "Fisherman’s risotto", price: "€18" },
                    { name: "Malloreddus with sea aroma", price: "€15" }
                ]
            },
            {
                title: "Main courses",
                items: [
                    { name: "Grilled catch", price: "€22" },
                    { name: "Grilled calamari", price: "€20" },
                    { name: "Salt-baked seabream", price: "€21" },
                    { name: "Seared tuna steak", price: "€23" },
                    { name: "Mixed fried seafood", price: "€19" }
                ]
            },
            {
                title: "Sides",
                items: [
                    { name: "Seasonal salad", price: "€6" },
                    { name: "Roasted potatoes", price: "€6" },
                    { name: "Grilled vegetables", price: "€7" },
                    { name: "Lemon greens", price: "€6" }
                ]
            }
        ],
        pizzaTitle: "Pizza Menu",
        pizzaItems: [
            { name: "Margherita", price: "€8" },
            { name: "Marinara", price: "€7" },
            { name: "Sarda", price: "€11" },
            { name: "Four cheese", price: "€11" },
            { name: "Tuna & onion", price: "€10" }
        ],
        wineTitle: "Sardinian wine list",
        wineSubtitle: "5 labels chosen to pair with the catch.",
        wines: [
            { name: "Vermentino di Gallura DOCG", detail: "Fresh and mineral" },
            { name: "Nuragus di Cagliari DOC", detail: "Delicate and floral" },
            { name: "Cannonau di Sardegna DOC", detail: "Soft and spicy" },
            { name: "Carignano del Sulcis DOC", detail: "Structured and intense" },
            { name: "Malvasia di Bosa DOC", detail: "Aromatic and velvety" }
        ],
        wineImageAlt: "Wine glass at sunset by the sea",
        bookTitle: "Book your table",
        bookSubtitle: "Quick reply via WhatsApp.",
        bookCta1: "Book now",
        bookCta2: "Call"
    },
    de: {
        title: "Mare Azzurro",
        metaTitle: "Mare Azzurro | Fischrestaurant",
        metaDescription: "Frischer Fisch und Tische mit Meerblick. Einfach per Handy reservieren.",
        intro: "Frischer Fisch, einfache Küche und Tische mit Meerblick. Ideal für Sommer-Mittag- und Abendessen.",
        menu: [
            { label: "Menü", href: "#menu" },
            { label: "Chef", href: "#chef" },
            { label: "Buchen", href: "#prenota" }
        ],
        ctaPrimary: "Tisch reservieren",
        ctaSecondary: "Menü ansehen",
        menuTitle: "Mare Azzurro Menü",
        menuSubtitle: "Einfache Gerichte, Meeresaromen und lokale Produkte.",
        menuSections: [
            {
                title: "Vorspeisen",
                items: [
                    { name: "Tagesfrische Rohkost", price: "€18" },
                    { name: "Thunfisch‑Tatar", price: "€16" },
                    { name: "Oktopus und Kartoffeln", price: "€14" },
                    { name: "Miesmuscheln Marinara", price: "€12" },
                    { name: "Meeresfrüchtesalat", price: "€15" }
                ]
            },
            {
                title: "Erste Gänge",
                items: [
                    { name: "Spaghetti mit Seeigel", price: "€18" },
                    { name: "Fregola mit Venusmuscheln", price: "€16" },
                    { name: "Linguine mit Meeresfrüchten", price: "€19" },
                    { name: "Fischer‑Risotto", price: "€18" },
                    { name: "Malloreddus mit Meeresduft", price: "€15" }
                ]
            },
            {
                title: "Hauptgänge",
                items: [
                    { name: "Fisch vom Grill", price: "€22" },
                    { name: "Gegrillter Tintenfisch", price: "€20" },
                    { name: "Dorade in Salzkruste", price: "€21" },
                    { name: "Kurz gebratenes Thunfischsteak", price: "€23" },
                    { name: "Gemischte Frittura", price: "€19" }
                ]
            },
            {
                title: "Beilagen",
                items: [
                    { name: "Saisonsalat", price: "€6" },
                    { name: "Ofenkartoffeln", price: "€6" },
                    { name: "Gegrilltes Gemüse", price: "€7" },
                    { name: "Zitronensalat", price: "€6" }
                ]
            }
        ],
        pizzaTitle: "Pizza Menü",
        pizzaItems: [
            { name: "Margherita", price: "€8" },
            { name: "Marinara", price: "€7" },
            { name: "Sarda", price: "€11" },
            { name: "Vier Käse", price: "€11" },
            { name: "Thunfisch & Zwiebel", price: "€10" }
        ],
        wineTitle: "Sardische Weinkarte",
        wineSubtitle: "5 Etiketten, perfekt zum Fisch des Tages.",
        wines: [
            { name: "Vermentino di Gallura DOCG", detail: "Frisch und mineralisch" },
            { name: "Nuragus di Cagliari DOC", detail: "Zart und floral" },
            { name: "Cannonau di Sardegna DOC", detail: "Weich und würzig" },
            { name: "Carignano del Sulcis DOC", detail: "Strukturiert und intensiv" },
            { name: "Malvasia di Bosa DOC", detail: "Aromatisch und samtig" }
        ],
        wineImageAlt: "Weinglas bei Sonnenuntergang am Meer",
        bookTitle: "Tisch reservieren",
        bookSubtitle: "Schnelle Antwort per WhatsApp.",
        bookCta1: "Jetzt buchen",
        bookCta2: "Anrufen"
    }
};

const dishCarouselImages = [
    "https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?q=80&w=1400&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1544025162-d76694265947?q=80&w=1400&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?q=80&w=1400&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1473093226795-af9932fe5856?q=80&w=1400&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1476224203421-9ac39bcb3327?q=80&w=1400&auto=format&fit=crop"
];

const dishCarouselAlts = [
    "Antipasto di mare",
    "Piatto di pesce grigliato",
    "Pasta ai frutti di mare",
    "Crudo di pesce fresco",
    "Secondo di mare con verdure"
];

const pizzaCarouselImages = [
    "https://images.unsplash.com/photo-1548365328-9f547fb09523?q=80&w=1400&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1542281286-9e0a16bb7366?q=80&w=1400&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1593504049359-74330189a345?q=80&w=1400&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?q=80&w=1400&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1513104890138-7c749659a591?q=80&w=1400&auto=format&fit=crop"
];

const pizzaCarouselAlts = [
    "Pizza margherita appena sfornata",
    "Pizza con ingredienti di mare",
    "Pizza con verdure estive",
    "Pizza con pomodoro fresco",
    "Pizza con mozzarella filante"
];

const wineGalleryImages = [
    "https://images.unsplash.com/photo-1510626176961-4b37d6af754b?q=80&w=1200&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1523362628745-0c100150b504?q=80&w=1200&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1470337458703-46ad1756a187?q=80&w=1200&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?q=80&w=1200&auto=format&fit=crop&sat=-20"
];

const wineGalleryAlts = [
    "Calici di vino bianco",
    "Vino rosso versato",
    "Calice di vino al tramonto",
    "Degustazione di vini sardi"
];

const AutoCarousel = ({ images, alts }) => {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        const id = setInterval(() => {
            setIndex((prev) => (prev + 1) % images.length);
        }, 3500);
        return () => clearInterval(id);
    }, [images.length]);

    return (
        <div className="relative h-80 overflow-hidden rounded-3xl border border-sky-200 bg-white shadow-lg md:h-[520px]">
            {images.map((src, idx) => (
                <img
                    key={src}
                    src={src}
                    alt={alts[idx]}
                    className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${idx === index ? "opacity-100" : "opacity-0"}`}
                    loading="lazy"
                />
            ))}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-sky-950/60 via-sky-950/10 to-transparent px-6 py-4 text-sm text-white">
                {alts[index]}
            </div>
        </div>
    );
};

const RistoranteMarePage = () => {
    const { language } = useTranslation();
    const t = copy[language] || copy.it;
    const [openSection, setOpenSection] = useState(t.menuSections[0]?.title || "");

    const meta = buildNoIndexMeta({
        title: t.metaTitle,
        description: t.metaDescription,
        path: "/estate-2026/ristorante-mare"
    });

    return (
        <>
            <DocumentMeta {...meta} />
            <div className="min-h-screen bg-sky-50 text-slate-900">
                <header className="relative px-6 pt-10 pb-10 md:px-12 lg:px-20">
                    <div className="absolute inset-x-0 top-0 h-64 bg-gradient-to-r from-sky-100 via-sky-200/60 to-blue-100"></div>
                    <div className="relative">
                        <ExampleLandingHeader title={t.title} items={t.menu} />
                        <p className="mt-6 text-lg text-slate-700">{t.intro}</p>
                        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                            <a href="#prenota" className="rounded-full bg-sky-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-sky-200">
                                {t.ctaPrimary}
                            </a>
                            <a href="#menu" className="rounded-full border border-sky-400 px-6 py-3 text-sm font-semibold text-sky-700">
                                {t.ctaSecondary}
                            </a>
                        </div>
                        <div className="mt-8 overflow-hidden rounded-3xl border border-sky-100 bg-white shadow-lg">
                            <img
                                src="https://images.unsplash.com/photo-1473093226795-af9932fe5856?q=80&w=1600&auto=format&fit=crop"
                                alt="Piatto di pesce in ristorante sul mare"
                                className="h-64 w-full object-cover md:h-96"
                                loading="lazy"
                            />
                        </div>
                    </div>
                </header>

                <main className="px-6 pb-24 md:px-12 lg:px-20">
                    <section id="menu" className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
                        <AutoCarousel images={dishCarouselImages} alts={dishCarouselAlts} />
                        <div className="flex h-80 flex-col rounded-3xl bg-white p-6 shadow-lg md:h-[520px] md:p-10">
                            <h2 className="text-2xl font-semibold">{t.menuTitle}</h2>
                            <p className="mt-2 text-sm text-slate-600">{t.menuSubtitle}</p>
                            <div className="mt-6 flex-1 overflow-auto pr-1">
                                <div className="grid gap-4">
                                    {t.menuSections.map((section) => (
                                        <div key={section.title} className="rounded-2xl border border-sky-100">
                                            <button
                                                type="button"
                                                onClick={() => setOpenSection((prev) => (prev === section.title ? "" : section.title))}
                                                className="flex w-full items-center justify-between px-4 py-3 text-left text-lg font-semibold text-sky-700"
                                                aria-expanded={openSection === section.title}
                                            >
                                                <span>{section.title}</span>
                                                <span className="text-sky-400">{openSection === section.title ? "−" : "+"}</span>
                                            </button>
                                            {openSection === section.title && (
                                                <div className="space-y-3 px-4 pb-4">
                                                    {section.items.map((item) => (
                                                        <div key={item.name} className="flex items-center justify-between border-b border-sky-100 pb-2">
                                                            <p className="font-medium">{item.name}</p>
                                                            <p className="text-sm text-slate-500">{item.price}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-6 rounded-2xl bg-sky-50 p-4">
                                    <h3 className="text-lg font-semibold text-sky-700">{t.pizzaTitle}</h3>
                                    <div className="mt-3 space-y-3">
                                        {t.pizzaItems.map((item) => (
                                            <div key={item.name} className="flex items-center justify-between border-b border-sky-100 pb-2">
                                                <p className="font-medium">{item.name}</p>
                                                <p className="text-sm text-slate-500">{item.price}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="mt-10 grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
                        <div className="flex h-80 flex-col rounded-3xl bg-white p-6 shadow-lg md:h-[520px] md:p-10">
                            <h2 className="text-2xl font-semibold">{t.pizzaTitle}</h2>
                            <p className="mt-2 text-sm text-slate-600">Impasti leggeri e pizze classiche.</p>
                            <div className="mt-6 flex-1 overflow-auto pr-1">
                                <div className="space-y-3">
                                    {t.pizzaItems.map((item) => (
                                        <div key={item.name} className="flex items-center justify-between border-b border-sky-100 pb-2">
                                            <p className="font-medium">{item.name}</p>
                                            <p className="text-sm text-slate-500">{item.price}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <AutoCarousel images={pizzaCarouselImages} alts={pizzaCarouselAlts} />
                    </section>

                    <section className="mt-10 grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
                        <div className="grid gap-4">
                            <AutoCarousel images={wineGalleryImages} alts={wineGalleryAlts} />
                        </div>
                        <div className="flex h-80 flex-col rounded-3xl bg-white p-6 shadow-lg md:h-[520px] md:p-10">
                            <h2 className="text-2xl font-semibold">{t.wineTitle}</h2>
                            <p className="mt-2 text-sm text-slate-600">{t.wineSubtitle}</p>
                            <div className="mt-6 flex-1 overflow-auto pr-1">
                                <div className="space-y-3">
                                    {t.wines.map((wine) => (
                                        <div key={wine.name} className="flex items-center justify-between border-b border-sky-100 pb-2">
                                            <div>
                                                <p className="font-medium">{wine.name}</p>
                                                <p className="text-xs text-slate-500">{wine.detail}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </section>

                    <section id="prenota" className="mt-10 rounded-3xl bg-white/95 p-6 text-slate-900 shadow-lg md:p-10">
                        <h2 className="text-2xl font-semibold">{t.bookTitle}</h2>
                        <p className="mt-2 text-gray-700">{t.bookSubtitle}</p>
                        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                            <a href="https://wa.me/393515699197" className="rounded-full bg-sky-500 px-6 py-3 text-sm font-semibold text-white">
                                {t.bookCta1}
                            </a>
                            <a href="tel:+393515699197" className="rounded-full border border-sky-500 px-6 py-3 text-sm font-semibold text-sky-700">
                                {t.bookCta2}
                            </a>
                        </div>
                    </section>
                    <SocialLinksSection className="mt-10" />
                </main>
                <FloatingWhatsAppButton />
            </div>
        </>
    );
};

export default RistoranteMarePage;
